import {TextField} from '@wandb/components';
import {assertUnreachable} from '@wandb/weave/common/util/types';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import React, {useCallback, useEffect, useRef} from 'react';

import * as Filters from '../../../util/filters';
import type * as FilterTypes from '../../../util/filterTypes';
import type * as RunTypes from '../../../util/runTypes';
import {useDebouncedValue} from '../../../util/useDebouncedValue';
import {
  type FilterValueSelectorCreatorProps,
  FilterValueSelectorDate,
  FilterValueSelectorTime,
} from '../../Filters/FilterValueSelector';
import {FilterSimpleValueSelectField} from '../../InfiniteScrollDropdown/FilterSimpleValueSelectField';
import {useRunFilterValueDropdown} from '../hooks/useRunFilterValueDropdown';
import {
  isSweepFilterKey,
  SweepValueSelectField,
} from '../SweepValueSelectField';

export type RunFilterValueSelectorCreatorProps =
  FilterValueSelectorCreatorProps<RunTypes.Key>;

type RunFilterValueSelectorProps = RunFilterValueSelectorCreatorProps & {
  entityName: string;
  projectName: string;
};

export function RunsFilterContainsInput({
  filter,
  setFilter,
}: {
  filter: FilterTypes.IndividualFilter<RunTypes.Key>;
  setFilter: (f: FilterTypes.IndividualFilter<RunTypes.Key>) => void;
}) {
  const initialValue = String(filter.value ?? '');
  const filterRef = useRef(filter);
  const setFilterRef = useRef(setFilter);
  // oxlint-disable-next-line react/refs
  filterRef.current = filter;
  // oxlint-disable-next-line react/refs
  setFilterRef.current = setFilter;
  const onDebouncedChange = useCallback((value: string) => {
    setFilterRef.current({
      ...filterRef.current,
      value,
      disabled: value === '',
    } as FilterTypes.IndividualFilter<RunTypes.Key>);
  }, []);
  const {value, debouncedSetValue, instantSetValue} = useDebouncedValue(
    initialValue,
    300,
    onDebouncedChange
  );

  useEffect(() => {
    instantSetValue(initialValue);
  }, [initialValue, instantSetValue]);

  return (
    <div className="min-w-0 flex-grow max-w-[600px]">
      <TextField
        aria-label="Substring to match"
        data-test="filter-value"
        placeholder="Substring to match"
        fullWidth
        value={value}
        onChange={v => debouncedSetValue(v)}
      />
    </div>
  );
}

export const RunsFilterValueSelector = ({
  entityName,
  projectName,
  filter,
  setFilter,
  isValid = true,
}: RunFilterValueSelectorProps) => {
  const setPartial = (
    partialFilter: Partial<FilterTypes.IndividualFilter<RunTypes.Key>>
  ) => {
    setFilter({
      ...filter,
      ...partialFilter,
    } as FilterTypes.IndividualFilter<RunTypes.Key>);
  };

  const filterValueType = Filters.getFilterValueType(filter);
  switch (filterValueType) {
    case Filters.ValueType.Duration:
      return (
        <FilterValueSelectorTime
          meta={Filters.assertIsValueFilter(filter).meta}
          value={filter.value as string}
          setFilter={setPartial}
          minimumUnit={
            filter.op === 'WITHINSECONDS' ? 'minutes' : null
            /* the withinseconds operation isn't guaranteed to be accurate
                 on the scale of seconds and there isn't a known use case. */
          }
        />
      );
    case Filters.ValueType.Date:
      return (
        <FilterValueSelectorDate
          value={filter.value as string}
          setFilter={setPartial}
        />
      );
    case Filters.ValueType.Tags:
      // `FilterEditable` renders a tag row's operator and value fields itself
      // and never asks for a value selector.
      return null;
    case Filters.ValueType.Default:
      if (filter.op === 'CONTAINS') {
        return (
          <RunsFilterContainsInput filter={filter} setFilter={setFilter} />
        );
      }
      return (
        <RunsFilterSimpleValueSelector
          entityName={entityName}
          projectName={projectName}
          filter={filter}
          setFilter={setFilter}
          isValid={isValid}
        />
      );
    default:
      assertUnreachable(filterValueType);
  }
};

type RunsFilterSimpleValueSelectorProps = {
  entityName: string;
  projectName: string;
  filter: FilterTypes.IndividualFilter<RunTypes.Key>;
  setFilter(filter: FilterTypes.IndividualFilter<RunTypes.Key>): void;
  isValid?: boolean;
};
const RunsFilterSimpleValueSelector = (
  props: RunsFilterSimpleValueSelectorProps
) => {
  const {filter, setFilter, isValid = true, entityName, projectName} = props;
  const dropdown = useRunFilterValueDropdown({filter, setFilter});

  if (dropdown.shouldRenderNull) {
    return <TailwindContents />;
  }

  if (isSweepFilterKey(filter)) {
    return (
      <TailwindContents>
        <SweepValueSelectField
          className="max-w-[600px]"
          data-test="filter-value"
          entityName={entityName}
          projectName={projectName}
          skip={dropdown.keyPath === ''}
          isMultiSelect={dropdown.isMultiSelect}
          selections={dropdown.selections}
          onSelectionsChange={dropdown.setSelections}
          isValid={isValid}
        />
      </TailwindContents>
    );
  }

  return (
    <TailwindContents>
      <FilterSimpleValueSelectField
        className="max-w-[600px]"
        data-test="filter-value"
        entityName={entityName}
        projectName={projectName}
        extraQueryVariables={dropdown.extraQueryVariables}
        skip={dropdown.keyPath === ''}
        isMultiSelect={dropdown.isMultiSelect}
        selections={dropdown.selections}
        onSelectionsChange={dropdown.setSelections}
        isValid={isValid}
      />
    </TailwindContents>
  );
};
